/**
 * MV2 React Query cache persistence — instant paint after reload (stale-while-revalidate).
 */
import { dehydrate, hydrate } from "@tanstack/react-query";

const SCHEMA = 1;
const MAX_AGE_MS = 12 * 60 * 60 * 1000;
const MAX_PAYLOAD_BYTES = 3_000_000;
const SAVE_DEBOUNCE_MS = 1000;

export function mv2CacheStorageKey(key) {
  return `ttt_mv2_cache_${key}`;
}

export function clearAllMv2PersistedCaches() {
  if (typeof localStorage === "undefined") return;
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith("ttt_mv2_cache_")) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

export function setupMv2CachePersist(client, key) {
  if (typeof window === "undefined" || !client || !key) return;

  try {
    const raw = localStorage.getItem(mv2CacheStorageKey(key));
    if (raw) {
      const { v, ts, state } = JSON.parse(raw);
      if (v === SCHEMA && Date.now() - ts < MAX_AGE_MS) {
        hydrate(client, state);
      } else {
        localStorage.removeItem(mv2CacheStorageKey(key));
      }
    }
  } catch {
    try {
      localStorage.removeItem(mv2CacheStorageKey(key));
    } catch {
      /* ignore */
    }
  }

  let saveTimer;
  client.getQueryCache().subscribe(() => {
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => {
      try {
        const payload = JSON.stringify({
          v: SCHEMA,
          ts: Date.now(),
          state: dehydrate(client),
        });
        if (payload.length <= MAX_PAYLOAD_BYTES) {
          localStorage.setItem(mv2CacheStorageKey(key), payload);
        }
      } catch {
        /* quota exceeded — skip silently */
      }
    }, SAVE_DEBOUNCE_MS);
  });
}
